import { useTranslator } from '@eo-locale/preact';
import { ReviewSorting, VerificationStatus } from 'types/review';
import { ExtendedCarouselWidgetSettings } from 'types/settings';
import { useReviews } from 'utilities/api';

import { useSettings } from 'components/SettingsProvider';
import { Slider } from 'components/Slider';

import { SkeletonContent } from './components/SkeletonContent';
import { VerticalSlide } from './components/VerticalSlide';

import slideStyles from './components/VerticalSlide/VerticalSlide.module.scss';

interface CarouselWidgetProps {
  onRemove: () => void;
}

export function CarouselWidget({ onRemove }: CarouselWidgetProps) {
  const { translate } = useTranslator();
  const {
    total_reviews,
    reviews_count_on_slide,
    product_ids,
    filter_by_min_rating,
    filter_by_verified,
    filter_with_images,
    utils: { locale },
  } = useSettings<ExtendedCarouselWidgetSettings>();

  const { reviews, isLoading } = useReviews({
    components: {
      images: null,
      rating_details: null,
    },
    filters: {
      has_images: filter_with_images ? true : null,
      product_ids,
      rating_from: filter_by_min_rating,
      rating_to: 5,
      verified: filter_by_verified ? VerificationStatus.VERIFIED : null,
    },
    locale,
    count: total_reviews,
    page: 1,
    sort: ReviewSorting.DATE_DESC,
  });

  if (isLoading) {
    return <SkeletonContent />;
  }

  const autoMode = Boolean(reviews_count_on_slide === 0);

  const slidesMarkup = reviews.map((review) => {
    return <VerticalSlide key={review.id} review={review} />;
  });

  if (reviews.length === 0) onRemove();

  return reviews.length > 0 ? (
    <div>
      <Slider
        slidesCount={reviews.length}
        slidesPerView={autoMode ? 4 : reviews_count_on_slide}
        heading={translate('widget_carousel_title', {
          defaultMessage: 'Reviews from our customers',
        })}
        slideSelector={slideStyles.SlideWrapper}
        minSlideWidth={270}
        maxSlideWidth={autoMode ? 430 : 0}
      >
        {slidesMarkup}
      </Slider>
    </div>
  ) : null;
}
